// @flow
import type ApplicationModel from "./application/ApplicationModel";
import type BooleanAttributeModel from "./attributes/BooleanAttributeModel";
import type CaptchaAttributeModel from "./attributes/CaptchaAttributeModel";
import type ChoiceAttributeModel from "./attributes/ChoiceAttributeModel";
import type CompositeAttributeModel from "./attributes/CompositeAttributeModel";
import type DatetimeAttributeModel from "./attributes/DatetimeAttributeModel";
import type HelptextAttributeModel from "./attributes/HelptextAttributeModel";
import type LabelAttributeModel from "./attributes/LabelAttributeModel";
import type MemoAttributeModel from "./attributes/MemoAttributeModel";
import type MoneyAttributeModel from "./attributes/MoneyAttributeModel";
import type NumberAttributeModel from "./attributes/NumberAttributeModel";
import type PasswordAttributeModel from "./attributes/PasswordAttributeModel";
import type StringAttributeModel from "./attributes/StringAttributeModel";
import type UploadAttributeModel from "./attributes/UploadAttributeModel";
import type XMLAttributeModel from "./attributes/XMLAttributeModel";
import type CaseViewModel from "./caseview/CaseViewModel";
import type BusinessScenarioModel from "./concepts/BusinessScenarioModel";
import type ConceptDetailModel from "./concepts/ConceptDetailModel";
import type ConceptIndexModel from "./concepts/ConceptIndexModel";
import type ConceptTypeDetailModel from "./concepts/ConceptTypeDetailModel";
import type ContentIndexModel from "./content/ContentIndexModel";
import type ContentModel from "./content/ContentModel";
import type SectionModel from "./content/SectionModel";
import type ContentTOCModel from "./content/ContentTOCModel";
import type ContentTypeModel from "./content/ContentTypeModel";
import type DetailModel from "./detail/DetailModel";
import type FormModel from "./form/FormModel";
import type ListDetailModel from "./list/ListDetailModel";
import type ListModel from "./list/ListModel";
import type ModelCatalogModel from "./modelcatalog/ModelCatalogModel";
import type GroupingPanelModel from "./panels/GroupingPanelModel";
import type CaseSearchModel from "./search/CaseSearchModel";
import type TabModel from "./tab/TabModel";
import type TaskGroupModel from "./taskgroup/TaskGroupModel";
import type UserModel from "./user/UserModel";
import type UserServicesModel from "./user/UserServicesModel";
import type LinkModel from "./links/LinkModel";
import type LookupOptionsModel from "./lookup/LookupOptionsModel";
import type AttributeCollection from "./attributes/AttributeCollection";
import type AttributeModel from "./attributes/AttributeModel";
import type LayoutHintCollection from "./layouthint/LayoutHintCollection";
import type ErrorResponse from "./error/ErrorResponse";
import { PARAMETER_TYPES } from "../constants";

export type ModularUIModel =
  | ApplicationModel
  | CaseSearchModel
  | CaseViewModel
  | ListDetailModel
  | DetailModel
  | FormModel
  | GroupingPanelModel
  | ListModel
  | TabModel
  | TaskGroupModel
  | UserModel
  | UserServicesModel
  | ModelCatalogModel
  | ConceptIndexModel
  | ConceptDetailModel
  | BusinessScenarioModel
  | ConceptTypeDetailModel
  | ContentIndexModel
  | ContentTOCModel
  | ContentModel
  | ContentTypeModel
  | LookupOptionsModel;

export type AttributeType =
  | BooleanAttributeModel
  | CaptchaAttributeModel
  | ChoiceAttributeModel
  | DatetimeAttributeModel
  | HelptextAttributeModel
  | LabelAttributeModel
  | MemoAttributeModel
  | MoneyAttributeModel
  | NumberAttributeModel
  | PasswordAttributeModel
  | CompositeAttributeModel
  | StringAttributeModel
  | UploadAttributeModel
  | XMLAttributeModel;

export type RangeChildAttributeType =
  | NumberAttributeModel
  | DatetimeAttributeModel;

export type FormErrorAnchor = {
  id: string,
  properties?: {
    [propertyName: string]: string | number,
  },
  anchor?: {
    objectid: string,
    elementid?: string,
    _links?: Object,
    index?: number,
    "index-identifier"?: string,
  },
  layouthint: LayoutHintCollection,
  message: string,
  param?: {
    name: string,
  },
};

export type labelsJSON = {
  _id: string,
  label: string,
  value: string,
};

export type propertyJSON = {
  _id: string,
  type: string,
  mandatory: string,
  label: string,
  value: string,
};

export type textfragmentJSON = {
  label: string,
  text: string,
  type: string,
};

export interface IFilter {
  +type: string;
  +parameterType: $Values<typeof PARAMETER_TYPES>;
  +isMultiple: boolean;
  context: Object;
  +contextid: string;
  +contextLabel: string;
  listkey: string;
  +label: string;
  +key: string;
  +name: string;
  +param: string;
  +params: Array<{ name: string, value: ?string }>;
  isQuickSearch(): boolean;
  +assistantMessage: string | null;
  +value: string | { [string]: string } | null;
  +inputvalue: string | { [string]: string };
  hasValue(): boolean;
  reset(): IFilter;
  update(attribute: AttributeType, value: string): void;
  isActive(): boolean;
  +isValid: boolean;
  +readonlyvalue: string | { [string]: string };
  +formdata: { [string]: any } | null;
  clone(): IFilter;
}

export interface IConstraintModel {
  +id: string;
  +defaultMessage: string;
  +parameters: Object;
  +isMandatoryConstraint: boolean;
  validate(value: any): boolean;
  hasValidation(): boolean;
}

export interface ILayoutHintRule {
  process(attribute: AttributeModel, attributes: AttributeCollection): void;
}

export interface IModelWithChildModels {
  getInitialChildModelLinks(): Array<LinkModel>;
  setChildModels(
    models: Array<ModularUIModel>,
    errors: Array<ErrorResponse>,
  ): void;
}

export type AuthenticationType = {
  name: string,
  authentication: string,
  redirectUri?: string,
  isPrimary: boolean,
};

export type FileEntryType = {
  name: string,
  size?: number,
  progress: number,
  error?: string,
  token?: string,
};

export type FilesType = {
  [filename: string]: FileEntryType,
};

export type FiletypeConstraintsType = Array<{|
  extensions: Array<string>,
  mimeTypes: Array<string>,
|}>;

export type FilesizeConstraintsType = {
  fileSize: ?number,
  maxTotalFileSize: ?number,
  isMaxTotal: boolean,
};

export type PropertyData = { type: string, label: string, value: string };
export type TextFragmentData = {
  type: string,
  label: string,
  text: string | { id?: string, message: string, properties?: Object },
};
export type SectionData = {
  id: string,
  type: string,
  label?: string,
  number?: string,
  body: string | { id?: string, message: string, properties?: Object },
  _links: { self: { href: string } },
  childSections?: Array<SectionData>,
  sections?: Array<SectionData>,
  subSections: Array<SectionData>,
};

export type PropertyElement = {
  propertyElement: {
    label: string,
    layouthint: Array<string>,
    properties: Array<PropertyData>,
  },
};
export type TextFragmentElement = {
  textFragmentElement: {
    label: string,
    layouthint: Array<string>,
    textfragments: Array<TextFragmentData>,
  },
};
export type ContentElement = {
  contentElement: {
    label: string,
    layouthint: Array<string>,
    sections: Array<SectionData>,
  },
};

export type PropertyElementMapped = {
  propertyElement: {
    label: string,
    layouthint: LayoutHintCollection,
    properties: Array<PropertyData>,
  },
};
export type TextFragmentElementMapped = {
  textFragmentElement: {
    label: string,
    layouthint: LayoutHintCollection,
    textfragments: Array<TextFragmentData>,
  },
};
export type ContentElementMapped = {
  contentElement: {
    label: string,
    layouthint: LayoutHintCollection,
    sections: Array<SectionModel>,
  },
};

export type ContentAll = Array<
  PropertyElementMapped | TextFragmentElementMapped | ContentElementMapped,
>;

export type ContentData = {
  header: {
    label?: string,
    description?: { id?: string, message: string, properties?: Object },
  },
  label?: string,
  elements: Array<PropertyElement | TextFragmentElement | ContentElement>,
};

export type SectionFragment = {
  text: string,
  startOffset: number,
  endOffset: number,
};

export type ModelOptions = {
  origin?: ?string,
  contextPath?: ?string,
};
